import {
    createContext,
    createElement,
    type ReactNode,
    useContext,
    useSyncExternalStore,
} from 'react';
import { z } from 'zod';

import { type Mode } from '@libraries/typescript/api_client';

const STORAGE_KEY = 'phoebe-mode';

export const modeSchema = z.enum(['live', 'sandbox']);
const ModeOverrideContext = createContext<Mode | null>(null);

function parseMode(value: unknown): Mode {
    const result = modeSchema.safeParse(value);
    return result.success ? result.data : 'live';
}

let currentMode: Mode | null = null;
let channel: BroadcastChannel | null = null;
let storageHandler: ((event: StorageEvent) => void) | null = null;
const listeners = new Set<() => void>();
type ModeChangeGuard = (
    proceed: () => void,
    cancel: () => void,
    signal: AbortSignal,
) => boolean;
const changeGuards = new Set<ModeChangeGuard>();

let modeChangeRevision = 0;
let pendingModeChange: {
    readonly revision: number;
    readonly persist: boolean;
    readonly controller: AbortController;
} | null = null;

function cancelPendingModeChange() {
    const pending = pendingModeChange;
    pendingModeChange = null;
    pending?.controller.abort();
}

function requestModeChange(
    mode: Mode,
    persist: boolean,
    onChanged?: () => void,
) {
    // Another tab must not replace the mode in a local context switch awaiting confirmation.
    if (!persist && pendingModeChange?.persist) {
        return;
    }
    if (mode === ensureCurrentMode()) {
        if (pendingModeChange && !pendingModeChange.persist) {
            cancelPendingModeChange();
        }
        onChanged?.();
        return;
    }
    cancelPendingModeChange();
    const revision = ++modeChangeRevision;
    const controller = new AbortController();
    pendingModeChange = { revision, persist, controller };
    const cancel = () => {
        if (pendingModeChange?.revision === revision) {
            cancelPendingModeChange();
        }
    };
    const proceed = () => {
        if (pendingModeChange?.revision !== revision) {
            return;
        }
        pendingModeChange = null;
        currentMode = mode;
        if (persist) {
            try {
                localStorage.setItem(STORAGE_KEY, mode);
            } catch {
                // Private browsing or storage quota can make persistence unavailable.
            }
            channel?.postMessage(mode);
        }
        emitChange();
        onChanged?.();
    };
    for (const guard of changeGuards) {
        if (guard(proceed, cancel, controller.signal)) {
            return;
        }
    }
    proceed();
}

function readStoredMode(): Mode {
    let storedMode: string | null = null;
    try {
        storedMode = localStorage.getItem(STORAGE_KEY);
    } catch {
        // localStorage unavailable (private browsing, etc.)
    }

    return parseMode(storedMode);
}

function ensureCurrentMode(): Mode {
    if (currentMode === null) {
        currentMode = readStoredMode();
    }

    return currentMode;
}

function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}

export const modeStore = {
    getMode(): Mode | undefined {
        return ensureCurrentMode();
    },
    setMode(mode: Mode, onChanged?: () => void) {
        requestModeChange(mode, true, onChanged);
    },
    registerChangeGuard(guard: ModeChangeGuard) {
        changeGuards.add(guard);
        return () => {
            changeGuards.delete(guard);
        };
    },
    activate() {
        ensureCurrentMode();

        if (channel !== null) {
            return;
        }

        channel = new BroadcastChannel('phoebe-mode');
        channel.onmessage = (event) => {
            const newMode = parseMode(event.data);
            requestModeChange(newMode, false);
        };

        storageHandler = (event) => {
            if (event.key === STORAGE_KEY && event.newValue !== null) {
                const newMode = parseMode(event.newValue);
                requestModeChange(newMode, false);
            }
        };
        window.addEventListener('storage', storageHandler);
    },
    deactivate() {
        currentMode = null;
        cancelPendingModeChange();

        if (channel) {
            channel.close();
            channel = null;
        }

        if (storageHandler) {
            window.removeEventListener('storage', storageHandler);
            storageHandler = null;
        }
    },
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};

export function ModeOverrideProvider({
    mode,
    children,
}: {
    readonly mode: Mode | null;
    readonly children?: ReactNode;
}) {
    return createElement(
        ModeOverrideContext.Provider,
        { value: mode },
        children,
    );
}

export function useMode(): Mode | undefined {
    const overriddenMode = useContext(ModeOverrideContext);
    const storedMode = useSyncExternalStore(
        modeStore.subscribe,
        modeStore.getMode,
    );

    return overriddenMode ?? storedMode;
}
